import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchBlog } from "@/lib/api";
import LoadingState from "@/components/LoadingState";
import { ArrowUpRight } from "lucide-react";

export default function Blog() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("All");

    useEffect(() => {
        setLoading(true);
        fetchBlog({ limit: 50 })
            .then(setArticles)
            .catch(() => setArticles([]))
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const s = new Set(articles.map(a => a.category));
        return ["All", ...Array.from(s)];
    }, [articles]);

    const filtered = category === "All" ? articles : articles.filter(a => a.category === category);
    const hero = filtered[0];
    const rest = filtered.slice(1);

    return (
        <div data-testid="blog-page" className="container-tight py-16 md:py-24">
            <div className="border-b border-zinc-800 pb-10 mb-10">
                <div className="eyebrow">The Blog</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mt-3">
                    Buying guides, <span className="text-zinc-500">explainers, and</span> long reads.
                </h1>
                <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
                    Deep dives into printer economics, how-tos, and the occasional rant about wireless setup.
                </p>
            </div>

            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-categories">
                {categories.map(c => (
                    <button
                        key={c}
                        onClick={() => setCategory(c)}
                        data-testid={`category-tab-${c}`}
                        className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors duration-200 ${
                            category === c
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-zinc-600 hover:text-white"
                        }`}
                    >
                        {c}
                    </button>
                ))}
            </div>

            {loading ? (
                <LoadingState label="Loading articles" />
            ) : filtered.length === 0 ? (
                <div className="card-flat p-12 text-center text-zinc-400">No articles in this category yet.</div>
            ) : (
                <>
                    {/* Hero article */}
                    {hero && (
                        <Link
                            to={`/blog/${hero.slug}`}
                            data-testid="blog-hero"
                            className="group card-flat grid grid-cols-1 lg:grid-cols-5 overflow-hidden hover:border-zinc-600 transition-colors duration-200 mb-12"
                        >
                            <div className="lg:col-span-3 aspect-video lg:aspect-auto bg-zinc-900 overflow-hidden">
                                <img src={hero.image} alt={hero.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            </div>
                            <div className="lg:col-span-2 p-8 md:p-10 flex flex-col justify-center">
                                <div className="flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase">
                                    <span className="text-blue-400">{hero.category}</span>
                                    <span className="text-zinc-500">·</span>
                                    <span className="text-zinc-500">{hero.read_minutes} min read</span>
                                </div>
                                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mt-4 leading-tight group-hover:text-blue-400 transition-colors">
                                    {hero.title}
                                </h2>
                                <p className="text-zinc-400 mt-4 leading-relaxed">{hero.excerpt}</p>
                                <div className="mt-6 inline-flex items-center gap-1 text-sm text-blue-400 font-medium">
                                    Read the article <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                                </div>
                            </div>
                        </Link>
                    )}

                    {/* Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="article-grid">
                        {rest.map(a => (
                            <Link
                                key={a.id}
                                to={`/blog/${a.slug}`}
                                data-testid={`article-card-${a.slug}`}
                                className="group card-flat overflow-hidden hover:border-zinc-600 transition-colors duration-200"
                            >
                                <div className="aspect-video bg-zinc-900 overflow-hidden">
                                    <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase">
                                        <span className="text-blue-400">{a.category}</span>
                                        <span className="text-zinc-500">·</span>
                                        <span className="text-zinc-500">{a.read_minutes} min</span>
                                    </div>
                                    <h3 className="font-display text-lg font-semibold tracking-tight text-white mt-3 leading-snug group-hover:text-blue-400 transition-colors">{a.title}</h3>
                                    <p className="text-sm text-zinc-400 mt-3 leading-relaxed line-clamp-2">{a.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
